import Grid from '@mui/material/Grid';
import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import { errorMessage } from 'constants/CommonConstants';
import { formRequiredRules } from 'helper';
import React, { useImperativeHandle } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { CommonInput } from 'sharedComponents/inputGroup';
import { InputRadio } from 'sharedComponents/inputGroup/InputRadio';
import { postLeadCreation } from 'store/LeadCreationState';
import { RootState } from 'store/store';
import { EMPLOYMENT_STATUS_OPTION, SCHOOL_OPTION } from '../LeadGenConstant';
import { Lead } from '../LeadGenInterface';
import { StepperFromProps } from 'sharedComponents/dynamicStepperForm/dynamicStepperFormInterface';

const EmploymentStatus: React.ForwardRefRenderFunction<unknown, StepperFromProps> = (
  { onNext, onBack, isSubmit, mappedData },
  ref
) => {
  // Hooks
  const { t } = useTranslation();

  const hasCalledNextStep = React.useRef(false);

  // Redux
  const dispatch: ThunkDispatch<RootState, AnyAction, AnyAction> = useDispatch();

  const { authenticateClientData } = useSelector((state: RootState) => state.auth) || {};
  const widgetData = authenticateClientData?.data?.widgetData || {};
  const taxDeductionStepIndex = widgetData?.stepConfig?.indexOf('TaxDeductionStep');

  // React Hook Form
  const { control, trigger, watch, getValues } = useFormContext();

  // React Hook Form Watcher
  const employmentStatusWatcher = watch('employmentStatus');

  // React Hook Form Trigger
  const nextStep = async () => {
    if (hasCalledNextStep.current) return;
    hasCalledNextStep.current = true;

    const { employmentStatus, incomeSource, workingWhileInSchool } = getValues();

    const EmploymentData: Lead = {
      leadSourceId: widgetData?.leadSourceId,
      isSubmit,
      leadDetails: {
        'Employment Status': employmentStatus,
        'Income Source': employmentStatus === 'Other' ? incomeSource : undefined,
        'Working While In School': employmentStatus === 'Student' ? workingWhileInSchool : undefined,
        ...mappedData,
      },
    };

    dispatch(postLeadCreation({ data: EmploymentData, leadTypeID: widgetData?.leadTypeId })).then((res) => {
      if (
        res?.payload?.success &&
        (res?.payload?.data?.draftLeadId || res?.payload?.data?._id || res?.payload?.data?.Lead?.leadId)
      ) {
        const isNotOtherOrStudent = employmentStatus === 'Student' && workingWhileInSchool === 'no';
        const addressStepIndex = widgetData?.stepConfig?.indexOf('AddressStep');

        // const step = isNotOtherOrStudent
        //   ? 9 //AddressStep
        //   : 5; //TaxDeductionStep

        isNotOtherOrStudent
          ? addressStepIndex > -1
            ? onNext?.(addressStepIndex) // 9 AddressStep
            : onNext?.()
          : taxDeductionStepIndex > -1
            ? onNext?.(taxDeductionStepIndex) // 5 TaxDeductionStep
            : onNext?.();

        // onNext?.(step);
      }
    });
  };

  const backStep = async () => {
    onBack?.();
  };

  useImperativeHandle(ref, () => ({
    nextStep,
    backStep,
  }));

  return (
    <>
      <h3 className='dy-stepper-form__body__title text-center'>{t('leadGen.empStatusTxt')}</h3>
      <InputRadio
        rules={formRequiredRules(errorMessage(t).radioError)}
        name={'employmentStatus'}
        control={control}
        options={EMPLOYMENT_STATUS_OPTION(t)}
        directionRow={false}
        triggerValidationForField={(name: string, val: string) => {
          trigger(name);
          // val !== 'Other' && val !== 'Student' && taxDeductionStepIndex > -1
          //   ? onNext?.(taxDeductionStepIndex) // 5 TaxDeductionStep;
          //   : val !== 'Other' && val !== 'Student' && onNext?.();

          if (val !== 'Other' && val !== 'Student') {
            taxDeductionStepIndex > -1 ? onNext?.(taxDeductionStepIndex) : onNext?.();
          }
        }}
        itemPerRow={{
          xs: 12,
          sm: 6,
          lg: 6,
        }}
        radioVariant='outline'
      />

      {employmentStatusWatcher === 'Student' && (
        <Grid container justifyContent={{ xs: 'center' }} mt={5}>
          <Grid item xs={12} lg={8}>
            <h3 className='dy-stepper-form__body__title text-center'>{t('leadGen.empStatusTxt2')}</h3>
            <InputRadio
              rules={formRequiredRules(errorMessage(t).radioError)}
              name={'workingWhileInSchool'}
              control={control}
              options={SCHOOL_OPTION(t)}
              directionRow={false}
              triggerValidationForField={(name: string) => {
                trigger(name);
              }}
              radioVariant='outline'
              itemPerRow={{
                xs: 6,
              }}
            />
          </Grid>
        </Grid>
      )}

      {employmentStatusWatcher === 'Other' && (
        <Grid container justifyContent={{ xs: 'center' }} mt={5}>
          <Grid item xs={12} lg={8}>
            <h3 className='dy-stepper-form__body__title text-center'>{t('leadGen.empStatusTxt3')}</h3>
            <CommonInput
              name='incomeSource'
              control={control}
              label={t('leadGen.incomeSource')}
              placeholder={t('leadGen.incomeSource')}
              rules={formRequiredRules(errorMessage(t).incomesourceRequired)}
              triggerValidationForField={trigger}
            />
          </Grid>
        </Grid>
      )}
    </>
  );
};

const EmploymentStatusStep = React.forwardRef(EmploymentStatus);

export default EmploymentStatusStep;
